import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { MatTableDataSource } from '@angular/material/table';
import { Select } from '@ngxs/store';
import { PerformancePeriod } from 'app/library/performance-period/models/performance-period.model';
import { PerformancePeriodsState } from 'app/library/performance-period/models/performance-periods.state';
import { PerformancePeriodService } from 'app/library/performance-period/services/performance-period.service';
import { PerformanceTeamPersonalReview } from 'app/library/performance-personal-review/models/performance-team-personal-review.model';
import { PerformanceProfilePersonalReview } from 'app/library/performance-profile-personal-review/models/performance-profile-personal-review.model';
import { TeamService } from 'app/library/team/services/team.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { SimpleEntity } from 'app/projects/shared/src/lib/models/simple-entity.model';
import { GlobalParticipant } from 'app/projects/user/src/lib/models/participant.model';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { IPerformanceTeamReviewActionPayload } from '../../interfaces/create-performance-team-review.interface';
import { PerformanceTeamReview } from '../../models/performance-team-review.model';

@Component({
    selector: 'app-performance-team-review-dialog',
    templateUrl: './performance-team-review-dialog.component.html',
    styleUrls: ['./performance-team-review-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class PerformanceTeamReviewDialog extends UnsubscribeOnDestroy implements OnInit {
    @Select(PerformancePeriodsState.getFilteredEntities)
    filteredPerformancePeriods$: Observable<PerformancePeriod[]>;

    @Input()
    title: string;

    @Input()
    teamId: string;

    @Input()
    performanceTeamTemplateTypes: SimpleEntity[];

    @Input()
    performanceTeamReview: PerformanceTeamReview;

    @Input()
    performancePersonalReview: PerformanceProfilePersonalReview | PerformanceTeamPersonalReview;

    performancePeriods: PerformancePeriod[] = [];
    closestPeriod: PerformancePeriod;
    followUpPeriod: PerformancePeriod;

    displayedColumns = [];
    dataSource: MatTableDataSource<GlobalParticipant> = new MatTableDataSource([]);
    hideEmail: boolean;
    removable: boolean = true;
    list: GlobalParticipant[] = [];
    teamMembersUsers: GlobalParticipant[] = [];

    updateDueDate(): void {
        const startDate = this.formGroup.get('startDate').value;

        this.minDate = startDate ? new Date(startDate) : null;
    }

    updateStartDate(): void {
        const dueDate = this.formGroup.get('dueDate').value;

        this.maxDate = dueDate ? new Date(dueDate) : null;
    }

    private _formGroup: FormGroup;

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    minDate: Date;
    maxDate: Date;

    constructor(
        private _formBuilder: FormBuilder,
        private _performancePeriodService: PerformancePeriodService,
        private _teamService: TeamService,
        public dialogRef: MatDialogRef<PerformanceTeamReviewDialog>
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this._performancePeriodService.fetchPerformancePeriods();

        this.filteredPerformancePeriods$.subscribe((performancePeriods: PerformancePeriod[]) => {
            this.performancePeriods = performancePeriods;
        });

        this._formGroup = this._formBuilder.group({
            name: [this.performanceTeamReview ? this.performanceTeamReview.name : '', Validators.required],
            type: [
                {
                    value: this.performancePersonalReview ? this.performancePersonalReview.type.id : this.performanceTeamReview ? this.performanceTeamReview.type.id : '',
                    disabled: this.performancePersonalReview ? true : false,
                },
                Validators.required,
            ],
            startDate: [this.performanceTeamReview ? this.performanceTeamReview.startDate : '', Validators.required],
            dueDate: [this.performanceTeamReview ? this.performanceTeamReview.dueDate : '', Validators.required],
            periodUnderReview: [this.performanceTeamReview ? this.performanceTeamReview.periodUnderReview : ''],
            goalSettingPeriod: [this.performanceTeamReview ? this.performanceTeamReview.goalSettingPeriod : ''],
        });

        if (this.performanceTeamReview) {
            this.formGroup.get('type').disable();

            if (!this.performanceTeamReview.isStartDateInFuture) {
                this._formGroup.get('startDate').disable();
            }
        }

        this.teamMembersUsers = await this._teamService.fetchTeamMembersUsers(this.teamId);
    }

    goBack(stepper: MatStepper): void {
        stepper.previous();
    }

    goForward(stepper: MatStepper): void {
        stepper.next();
    }

    async onStepChange(event: MatStepper): Promise<void> {
        if (event.selectedIndex === 0) {
            // Remove the 'required' validator when not on step 2
            this.formGroup.get('periodUnderReview').clearValidators();
            this.formGroup.get('periodUnderReview').updateValueAndValidity(); // Refresh validation
        } else if (event.selectedIndex === 1) {
            // Add the 'required' validator to periodUnderReview when on second step
            this.formGroup.get('periodUnderReview').setValidators([Validators.required]);
            this.formGroup.get('periodUnderReview').updateValueAndValidity(); // Refresh validation

            const reviewStartDate = moment(this.formGroup.get('startDate').value);
            this.closestPeriod = this.getSelectedPeriod(this.performancePeriods, reviewStartDate);

            // Preselect closest period by setting the form control value
            if (this.closestPeriod) {
                this.formGroup.get('periodUnderReview').setValue(this.closestPeriod.id);
                this.formGroup.get('goalSettingPeriod').setValue(this.closestPeriod.id);
            }

            this.followUpPeriod = this.getFollowUpPeriod(this.closestPeriod);
        } else if (event.selectedIndex === 2) {
            // Populate the data source with the team members
            this.dataSource.data = this.teamMembersUsers;

            if (this.hideEmail) {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
            } else {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
            }

            this.list = [];
        }
    }

    addToList(user: GlobalParticipant): void {
        // Add the user to the list
        this.list = [...this.list, user];

        // Update the dataSource to reflect the selected state
        this.dataSource.data = this.dataSource.data.map((item) => {
            if (item.id === user.id || this.list.find((item2) => item2.id === item.id)) {
                return { ...item, selected: true }; // Mark as selected
            }
            return item;
        });
    }

    addAllToList(): void {
        // Add all users to the list
        this.list = [...this.dataSource.data];

        // Update the dataSource to reflect the selected state for all users
        this.dataSource.data = this.dataSource.data.map((item) => {
            return { ...item, selected: true }; // Mark all as selected
        });
    }

    removeFromList(user: GlobalParticipant): void {
        // Remove the user from the list
        this.list = this.list.filter((item) => item.id !== user.id);

        // Update the dataSource to reflect the deselected state
        this.dataSource.data = this.dataSource.data.map((item) => {
            if (item.id === user.id) {
                return { ...item, selected: false }; // Mark as deselected
            }
            return item;
        });
    }

    onPerformancePeriodSelected(performancePeriod: PerformancePeriod): void {
        this.closestPeriod = performancePeriod;
        this.followUpPeriod = this.getFollowUpPeriod(this.closestPeriod);
    }

    getSelectedPeriod(periods: PerformancePeriod[], reviewStartDate: moment.Moment): PerformancePeriod {
        let selectedPeriod: PerformancePeriod | null = null;

        for (let i = 0; i < periods.length; i++) {
            const period = periods[i];
            const periodStartDate = moment(period.startDate);
            const threeMonthsAfterPeriodStart = periodStartDate.clone().add(3, 'months');

            // Rule 1: If startDate is within the first 3 months of a period, select the previous period
            if (reviewStartDate.isBetween(periodStartDate, threeMonthsAfterPeriodStart, null, '[)')) {
                if (i > 0) {
                    selectedPeriod = periods[i - 1];
                } else {
                    selectedPeriod = period; // If no previous period, select the current one
                }
                break;
            }

            // Rule 2: If startDate is later than 3 months after the start of a period, select that period
            if (reviewStartDate.isAfter(threeMonthsAfterPeriodStart)) {
                selectedPeriod = period;
            }
        }

        // Rule 3: If no period is found, select the last period
        if (!selectedPeriod) {
            selectedPeriod = periods[periods.length - 1];
        }

        return selectedPeriod;
    }

    getFollowUpPeriod(closestPeriod: PerformancePeriod): PerformancePeriod | null {
        // Find the index of the closest period in the performancePeriods array
        const index = this.performancePeriods.findIndex((period) => period.id === closestPeriod.id);

        // Check if the next period exists in the list after the closest period
        if (index !== -1 && index <= this.performancePeriods.length - 1) {
            // Return the next period in the list, which is the period after the closestPeriod
            return this.performancePeriods[index - 1];
        }

        // If no next period is available, return null
        return null;
    }

    save(): void {
        const formData = this._formGroup.getRawValue();

        const data: IPerformanceTeamReviewActionPayload = {
            name: formData.name,
            startDate: moment(formData.startDate).format('YYYY-MM-DD'),
            dueDate: moment(formData.dueDate).format('YYYY-MM-DD'),
            type: {
                id: formData.type,
            },
            periodUnderReview: {
                id: formData.periodUnderReview,
            },
            goalSettingPeriod: {
                id: formData.goalSettingPeriod,
            },
            team: {
                id: this.teamId,
            },
            userIds: this.list.map((user) => user.id),
        };

        this.dialogRef.close(data);
    }
}
